import { getElements } from '../Utility/Dom'
import { reinitOnSwap } from '../Utility/SwapReinit'

interface ChildEntry {
  readonly element: HTMLLIElement
  readonly parentLabel: string
}

// Live-search inside filter panels.
// Default behaviour: filter the checklist of the current panel by case-insensitive
// text match (used by Wo).
// Was panel: also matches children inside `.search-filter__topics-children`
// blocks. Child hits are cloned into the parent list with a breadcrumb chip so
// the user sees a flat result list — Figma 2801-30323.
class CourseFilterSearch {
  private readonly inputs: NodeListOf<HTMLElement>

  constructor() {
    this.inputs = getElements('search-filter-search')
  }

  public init(): void {
    this.inputs.forEach((input): void => {
      if (!(input instanceof HTMLInputElement)) return

      const topicsContainer = input.closest<HTMLElement>('[data-js="search-filter-topics"]')
      if (topicsContainer) {
        this.initTopicsSearch(input, topicsContainer)
        return
      }

      const panel = input.closest('.search-filter__panel')
      if (!panel) return

      const items = panel.querySelectorAll<HTMLElement>('.search-filter__checklist > li')
      input.addEventListener('input', (): void => {
        const query = input.value.trim().toLowerCase()
        items.forEach((item): void => {
          const text = item.textContent?.toLowerCase() ?? ''
          item.hidden = query !== '' && !text.includes(query)
        })
      })
    })
  }

  private initTopicsSearch(input: HTMLInputElement, container: HTMLElement): void {
    const mainList = container.querySelector<HTMLElement>('.search-filter__topics-main .search-filter__checklist')
    if (!mainList) return

    const inPrefix = container.dataset.labelInPrefix ?? 'In:'

    const childPanels = container.querySelectorAll<HTMLElement>('.search-filter__topics-children')
    const childIndex: ChildEntry[] = []
    childPanels.forEach((panel): void => {
      const parentLabel = panel.querySelector('.search-filter__topics-children-title')?.textContent?.trim() ?? ''
      panel.querySelectorAll<HTMLLIElement>('li').forEach((li): void => {
        childIndex.push({ element: li, parentLabel })
      })
    })

    input.addEventListener('input', (): void => {
      const query = input.value.trim().toLowerCase()

      mainList.querySelectorAll('[data-search-injected]').forEach((node): void => {
        node.remove()
      })

      if (query === '') {
        mainList.querySelectorAll<HTMLLIElement>('li').forEach((li): void => {
          li.hidden = false
        })
        return
      }

      // Searching cancels drill-in mode — results are flat.
      container.classList.remove('is-split')
      childPanels.forEach((panel): void => {
        panel.hidden = true
      })

      mainList.querySelectorAll<HTMLLIElement>('li:not([data-search-injected])').forEach((li): void => {
        const text = li.textContent?.toLowerCase() ?? ''
        li.hidden = !text.includes(query)
      })

      childIndex.forEach(({ element, parentLabel }): void => {
        const text = element.textContent?.toLowerCase() ?? ''
        if (!text.includes(query)) return

        const clone = element.cloneNode(true) as HTMLLIElement
        clone.setAttribute('data-search-injected', '')
        clone.hidden = false

        const breadcrumb = document.createElement('span')
        breadcrumb.className = 'search-filter__breadcrumb'
        breadcrumb.textContent = `${inPrefix} ${parentLabel}`

        // Render the breadcrumb chip directly next to the sub-topic name,
        // before the count column.
        const textSpan = clone.querySelector('.search-filter__checklist-text')
        if (textSpan) {
          textSpan.appendChild(breadcrumb)
        } else {
          clone.appendChild(breadcrumb)
        }

        mainList.appendChild(clone)
      })
    })
  }
}

const courseFilterSearch = new CourseFilterSearch()
courseFilterSearch.init()
reinitOnSwap(() => new CourseFilterSearch())
